import React, { useEffect, useState } from "react";
import logo from "assets/img/logo.png";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "config";
import packageJson from "../../../package.json";

// Chakra imports
import {
  Box,
  Flex,
  Heading,
  Text,
  Link,
  keyframes,
  Icon,
  VStack,
  HStack,
  Container,
  useBreakpointValue,
  Collapse,
} from "@chakra-ui/react";
import MufynButton from "components/Button";
import {
  FaArrowLeft,
  FaChevronDown,
} from "react-icons/fa";

// ============================================
// ANIMATIONS
// ============================================

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(-10px) rotate(-2deg); }
  50% { transform: translateY(10px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(40px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  0% { opacity: 0; transform: translateX(-40px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  0% { opacity: 0; transform: translateX(40px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const cardReveal = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// ============================================
// FAQ ITEM COMPONENT
// ============================================

const FAQItem = ({ questionKey, answerKey, delay = 0, isOpen, onToggle }) => {
  const { t } = useTranslation();

  return (
    <Box
      w="100%"
      borderRadius="20px"
      bg="linear-gradient(135deg, rgba(15, 20, 30, 0.6) 0%, rgba(20, 30, 40, 0.4) 100%)"
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={isOpen ? "rgba(34, 197, 94, 0.4)" : "rgba(255, 255, 255, 0.08)"}
      boxShadow={isOpen
        ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(34, 197, 94, 0.1)"
        : "0 4px 16px rgba(0, 0, 0, 0.2)"
      }
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      overflow="hidden"
      animation={`${cardReveal} 0.6s ease-out ${delay}s both`}
      _hover={{
        borderColor: isOpen ? "rgba(34, 197, 94, 0.5)" : "rgba(255, 255, 255, 0.15)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 15px rgba(34, 197, 94, 0.08)",
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        p={{ base: 5, md: 6 }}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: "rgba(255, 255, 255, 0.02)" }}
        transition="background 0.2s"
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="semibold"
          color="white"
          pr={4}
        >
          {t(questionKey)}
        </Text>
        <Icon
          as={FaChevronDown}
          w={4}
          h={4}
          color={isOpen ? "#4ade80" : "gray.500"}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="all 0.3s ease"
          flexShrink={0}
        />
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box px={{ base: 5, md: 6 }} pb={{ base: 5, md: 6 }}>
          <Box
            h="1px"
            bg="linear-gradient(90deg, rgba(34, 197, 94, 0.3), transparent)"
            mb={4}
          />
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="gray.400"
            lineHeight="1.8"
          >
            {t(answerKey)}
          </Text>
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

function FAQPage() {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? true;

  // Browser language detection
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    const browserLang = getBrowserLanguage();
    if (browserLang !== 'en') i18n.changeLanguage(browserLang);
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "unset"; };
  }, []);

  const faqItems = [
    { questionKey: "faq.items.what_is.question", answerKey: "faq.items.what_is.answer" },
    { questionKey: "faq.items.account_required.question", answerKey: "faq.items.account_required.answer" },
    { questionKey: "faq.items.data_security.question", answerKey: "faq.items.data_security.answer" },
    { questionKey: "faq.items.ai.question", answerKey: "faq.items.ai.answer" },
    { questionKey: "faq.items.reports.question", answerKey: "faq.items.reports.answer" },
    { questionKey: "faq.items.adjustment.question", answerKey: "faq.items.adjustment.answer" },
    { questionKey: "faq.items.quick_add.question", answerKey: "faq.items.quick_add.answer" },
    { questionKey: "faq.items.free.question", answerKey: "faq.items.free.answer" },
    { questionKey: "faq.items.bank_connection.question", answerKey: "faq.items.bank_connection.answer" },
    { questionKey: "faq.items.platforms.question", answerKey: "faq.items.platforms.answer" },
    { questionKey: "faq.items.shared.question", answerKey: "faq.items.shared.answer" },
    { questionKey: "faq.items.cancel.question", answerKey: "faq.items.cancel.answer" },
    { questionKey: "faq.items.support.question", answerKey: "faq.items.support.answer" }
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Box minH="100vh" w="100%" position="relative" bg="#0a0d14" overflowX="hidden">
      {/* ============================================ */}
      {/* ANIMATED BACKGROUND */}
      {/* ============================================ */}
      <Box position="fixed" top="0" left="0" right="0" bottom="0" zIndex="0" pointerEvents="none">
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.12) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.08) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        <Box
          position="absolute" top="10%" left="5%"
          w={{ base: "200px", md: "350px" }} h={{ base: "200px", md: "350px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)"
          filter="blur(50px)" animation={`${float} 10s ease-in-out infinite`}
        />
        <Box
          position="absolute" bottom="20%" right="5%"
          w={{ base: "250px", md: "450px" }} h={{ base: "250px", md: "450px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(74, 222, 128, 0.12) 0%, transparent 70%)"
          filter="blur(70px)" animation={`${floatReverse} 12s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
          w={{ base: "400px", md: "700px" }} h={{ base: "400px", md: "700px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 60%)"
          filter="blur(100px)" animation={`${pulse} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0" opacity="0.03"
          backgroundImage={`linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`}
          backgroundSize="80px 80px"
        />
        <Box
          position="absolute" top="25%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite`} backgroundSize="200% 100%"
        />
      </Box>

      {/* ============================================ */}
      {/* NAVBAR */}
      {/* ============================================ */}
      <Box
        position="fixed" top="0" left="0" right="0" zIndex="100"
        pt="env(safe-area-inset-top)"
        bg="linear-gradient(to-b, rgba(10, 13, 20, 0.98), rgba(10, 13, 20, 0.95))"
        backdropFilter="blur(20px)"
        borderBottom="1px solid rgba(255, 255, 255, 0.05)"
      >
        <Container maxW="container.xl">
          <Flex h={{ base: "70px", md: "80px" }} align="center" justify="space-between" px={{ base: 4, md: 6 }}>
            <HStack spacing={3} animation={`${slideInLeft} 0.6s ease-out`} as={RouterLink} to="/auth/landing" _hover={{ textDecoration: "none" }}>
              <Box position="relative">
                <Box
                  position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
                  w="50px" h="50px" borderRadius="full"
                  bg="radial-gradient(circle, rgba(34, 197, 94, 0.3) 0%, transparent 70%)" filter="blur(8px)"
                />
                <img src={logo} width={isMobile ? "36px" : "44px"} alt="Mufyn Logo" style={{ position: "relative", zIndex: 1 }} />
              </Box>
              <Heading fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="white">
                {config.appName}
              </Heading>
            </HStack>

            <HStack spacing={{ base: 2, md: 4 }} animation={`${slideInRight} 0.6s ease-out`}>
              <MufynButton as={RouterLink} to="/auth/landing" variant="ghost" size={isMobile ? "sm" : "md"} leftIcon={<FaArrowLeft />}>
                {t("faq.back")}
              </MufynButton>
              <MufynButton as={RouterLink} to="/auth/signin" variant="primary" size={isMobile ? "sm" : "md"}>
                {t("landing.nav.signin")}
              </MufynButton>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <Box pt={{ base: "130px", md: "160px" }} pb={{ base: 8, md: 12 }} position="relative" zIndex="1">
        <Container maxW="container.md" px={{ base: 4, md: 6 }}>
          <VStack spacing={4} textAlign="center" animation={`${slideUp} 0.8s ease-out`}>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="extrabold"
              color="white"
            >
              {t("faq.title")}
            </Heading>
            <Text fontSize={{ base: "md", md: "lg" }} color="gray.400" maxW="500px">
              {t("faq.subtitle")}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* ============================================ */}
      {/* FAQ ITEMS */}
      {/* ============================================ */}
      <Box py={{ base: 4, md: 8 }} position="relative" zIndex="1">
        <Container maxW="container.md" px={{ base: 4, md: 6 }}>
          <VStack spacing={4}>
            {faqItems.map((item, index) => (
              <FAQItem
                key={index}
                questionKey={item.questionKey}
                answerKey={item.answerKey}
                delay={0.05 * index}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </VStack>
        </Container>
      </Box>

      {/* ============================================ */}
      {/* CONTACT SECTION */}
      {/* ============================================ */}
      <Box py={{ base: 12, md: 16 }} position="relative" zIndex="1">
        <Container maxW="container.sm" px={{ base: 4, md: 6 }}>
          <VStack
            spacing={5} textAlign="center" p={{ base: 6, md: 10 }}
            borderRadius="24px"
            bg="linear-gradient(135deg, rgba(15, 20, 30, 0.6) 0%, rgba(20, 30, 40, 0.4) 100%)"
            backdropFilter="blur(20px)" border="1px solid rgba(255, 255, 255, 0.1)"
          >
            <Heading fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="white">
              {t("faq.contact.title")}
            </Heading>
            <Text fontSize={{ base: "sm", md: "md" }} color="gray.400">
              {t("faq.contact.description")}
            </Text>
            <Link
              href={`mailto:contact@nelx.net`}
              color="#4ade80"
              fontWeight="semibold"
              fontSize={{ base: "md", md: "lg" }}
              _hover={{ color: "#22c55e", textDecoration: "underline" }}
              transition="color 0.2s"
            >
              contact@nelx.net
            </Link>
          </VStack>
        </Container>
      </Box>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <Box py={{ base: 8, md: 10 }} borderTop="1px solid rgba(255, 255, 255, 0.05)" position="relative" zIndex="1">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={4}>
            <HStack spacing={3}>
              <img src={logo} width="32px" alt="Mufyn" />
              <Text color="gray.500" fontSize="sm">
                © {new Date().getFullYear()} {config.appName}. {t("landing.footer.rights")}
              </Text>
            </HStack>
            <HStack spacing={6}>
              <Text color="gray.600" fontSize="xs" fontFamily="monospace">
                v{packageJson.version}
              </Text>
              <Link href="https://nelx.net" isExternal color="gray.500" fontSize="sm" _hover={{ color: "#4ade80" }} transition="color 0.2s">
                {t("landing.footer.by")} NELX
              </Link>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default FAQPage;
